import { ArrowDownward, ArrowUpward, CheckCircle } from "@mui/icons-material";
import { Box, Chip, CircularProgress, Stack, Tooltip, Typography } from "@mui/joy";
import Color from "color";
import { format } from "date-fns";
import { TaskType } from "griptonite-api-client/types/tasks";

import MiscTask from "@/assets/icons/MiscTask";
import { TaskSet } from "@/core/types";

import Avatar from "../Avatar";
import { StackLink } from "../Links";

const TaskCountChip = ({ type, count }: { type: TaskType; count: number }) => {
  const color = {
    SET: "success.500",
    STRIP: "danger.500",
    MISC: "#00C8D5",
  }[type];
  const icon = {
    SET: <ArrowUpward sx={{ width: 16 }} />,
    STRIP: <ArrowDownward sx={{ width: 16 }} />,
    MISC: <MiscTask sx={{ width: 16 }} />,
  }[type];
  return (
    <Chip
      size="sm"
      startDecorator={icon}
      sx={{
        backgroundColor: color,
        color: "white",
        fontWeight: "bold",
        borderRadius: 4,
        fontSize: "xs",
        paddingX: 0.5,
        paddingY: 0,
        minHeight: "18px",
        height: "18px",
        lineHeight: "14px",
        gap: 0,
      }}
    >
      {count}
    </Chip>
  );
};

export const taskSetColorMap = {
  BOULDER: "#0084FF",
  SPORT: "#9B00E4",
  "": "#D4D6DE",
};

export const TasksetEventCard = ({
  taskSet,
  expandedName,
  onContextMenu,
}: {
  taskSet: TaskSet;
  expandedName?: boolean;
  onContextMenu?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}) => {
  const color = taskSetColorMap[taskSet.climbType || ""];
  const label =
    taskSet.name ||
    {
      BOULDER: "Bouldering",
      SPORT: "Sport",
      "": "Housekeeping",
    }[taskSet.climbType || ""];
  const doneCount = taskSet.countsByStatus.DONE;
  const totalCount = taskSet.countsByStatus.TODO + taskSet.countsByStatus.IN_PROGRESS + doneCount;

  let dateLabel = "";
  if (expandedName) {
    dateLabel = format(taskSet.startDate, "do MMM");
    if (taskSet.startDate !== taskSet.endDate) {
      dateLabel += ` - ${format(taskSet.endDate, "do MMM")}`;
    }
  }

  return (
    <StackLink
      sx={{
        border: "1px solid",
        borderColor: color,
        borderRadius: "8px",
        boxShadow: "sm",
        textDecoration: "none",
        overflow: "hidden",
        margin: 0.5,
        backgroundColor: "white",
        flexShrink: 0,
        WebkitTouchCallout: "none",
      }}
      to="/gym/$venueUuid/schedule/set/$setUuid"
      params={{
        venueUuid: taskSet.venue,
        setUuid: taskSet.uuid,
      }}
      onContextMenu={onContextMenu}
      draggable={false}
    >
      <Stack
        sx={{
          backgroundColor: Color(color).alpha(0.1).string(),
          borderBottom: "1px solid",
          borderColor: color,
          paddingX: 0.75,
          paddingY: 0.5,
        }}
        justifyContent="space-between"
        direction="row"
      >
        <Typography level="body-xs" fontWeight="bold" lineHeight={1}>
          {label}
        </Typography>
        <Stack direction="row" gap={0.5} alignItems="center">
          {expandedName ? (
            <Typography level="body-xs" lineHeight={1}>
              {dateLabel}
            </Typography>
          ) : null}
          {taskSet.announceTime && (
            <Tooltip size="sm" title="Announced">
              <Typography level="body-xs" lineHeight={0}>
                📢
              </Typography>
            </Tooltip>
          )}
        </Stack>
      </Stack>
      <Box sx={{ background: "white" }}>
        <Stack sx={{ gap: 0.5, padding: 0.5, height: 24 }} direction="row">
          {taskSet.countsByType.SET ? (
            <TaskCountChip type="SET" count={taskSet.countsByType.SET} />
          ) : null}
          {taskSet.countsByType.STRIP ? (
            <TaskCountChip type="STRIP" count={taskSet.countsByType.STRIP} />
          ) : null}
          {taskSet.countsByType.MISC ? (
            <TaskCountChip type="MISC" count={taskSet.countsByType.MISC} />
          ) : null}
        </Stack>
        <Stack
          sx={{ padding: 0.25, ".collapsed &": { display: "none" } }}
          alignItems="center"
          direction="row"
        >
          <Stack alignItems="center" direction="row" gap={0.25}>
            {totalCount === taskSet.countsByStatus.DONE && totalCount > 0 ? (
              <CheckCircle sx={{ width: 16, height: 16, color: "success.500" }} />
            ) : (
              <CircularProgress
                determinate
                value={(taskSet.countsByStatus.DONE * 100) / (totalCount || 1)}
                sx={{
                  "--CircularProgress-size": "14px",
                  "--CircularProgress-trackThickness": "0px",
                  "--CircularProgress-progressThickness": "3px",
                }}
                color="neutral"
              />
            )}
            <Typography level="body-xs" fontWeight="bold" textColor="neutral.700">
              {taskSet.countsByStatus.DONE}/{totalCount}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            sx={{
              "& > *": {
                marginLeft: -0.75,
                "&:first-of-type": {
                  marginLeft: 0,
                },
              },
              flex: 1,
            }}
            justifyContent="flex-end"
          >
            {/* <Avatar
              user={{ uuid: "a", name: "Donald H", pictureUrl: null }}
              size={20}
            />
            <Avatar
              user={{ uuid: "a", name: "Cassim L", pictureUrl: null }}
              size={20}
            />
            <Avatar
              user={{ uuid: "a", name: "Red Blue", pictureUrl: null }}
              size={20}
            /> */}
            {taskSet.assignees.slice(0, 3).map((user) => (
              <Avatar user={user} size={20} />
            ))}
          </Stack>
        </Stack>
      </Box>
    </StackLink>
  );
};
